// src/utils/timezoneLabel.js
//
// "America/New_York" is how a timezone is stored. "EST" is how a dispatcher
// says it.
//
// The IANA name is the correct thing to keep in the database — it survives
// daylight saving changes and political redefinitions, which a fixed "EST"
// does not. But it is long, it carries an underscore, and nobody reads a load
// board thinking about which continent they are on. So it is stored long and
// shown short.
//
// The abbreviation is asked of the browser rather than kept in a lookup table,
// so it follows daylight saving on its own: the same zone reads EST in January
// and EDT in July, which is what the clock on the wall says.

/**
 * Short label for an IANA timezone, e.g. "America/New_York" → "EST" / "EDT".
 *
 * @param {string} timeZone  IANA name
 * @param {Date}   at        when to evaluate — matters across a DST boundary
 * @returns {string} the abbreviation, or a tidied version of the input when the
 *   browser cannot resolve it. Never throws: a bad zone in one tenant's
 *   settings must not take down the page that displays it.
 */
export function timezoneAbbreviation(timeZone, at = new Date()) {
  if (!timeZone) return '';

  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(at)
      .find(p => p.type === 'timeZoneName');

    if (part?.value) {
      // Zones with no common abbreviation come back as a GMT offset
      // ("GMT+5:30"). That is still far shorter and more useful than the IANA
      // name, so it is kept as-is.
      return part.value;
    }
  } catch {
    // An unknown or malformed zone — fall through to the tidied name.
  }

  // Last resort: "America/New_York" → "New York". Still better than showing a
  // path with an underscore in it.
  return String(timeZone).split('/').pop().replace(/_/g, ' ');
}

/** The same thing, ready to drop into a sentence: "Times in EST". */
export function timesInLabel(timeZone) {
  const abbr = timezoneAbbreviation(timeZone);
  return abbr ? `Times in ${abbr}` : 'Times loading…';
}
